import { getApi } from "../Services/apiService";
import { getShortDisplayName, isUnnamedFirstName } from "./name.jsx";
import {
  formatDashboardMemberDisplayName,
  getDashboardMemberNameParts,
} from "./dashboardMemberDisplayName";
import {
  calculateAge,
  getMemberAgeAtDeath,
  getMemberAgeLabel,
  getMemberDobTimestamp,
} from "./family-tree/dateAndTime";

/**
 * The "Desc Parent and Child(ren)" report.
 *
 * Two calls feed it. GET /report-management/descendant-parents-and-children
 * is the report proper: every descendant parent from generation 2 down who
 * has children (the Start/Top of Line couple is left out, and so is anyone
 * divorced), each with their children under them and an age on every child,
 * plus the report's own count of parents. Sample parent:
 *
 *   { parent_id, parent_name: "John Doe", parent_first_name: "John",
 *     parent_last_name: "Doe", parent_suffix: null, parent_special_name: null,
 *     parent_color_name: "Blue", parent_hex_code: "#0000FF",
 *     parent_generation_level: 2, parent_present_or_deceased: 1,
 *     children_count: 2,
 *     children: [ { id, first_name: "Jane", last_name: "Doe", suffix: null,
 *       special_name: null, age: 12, dob: "2014-06-15", present_or_deceased: 1,
 *       color_name: "Blue", hex_code: "#0000FF", generation_level: 3 } ] }
 *
 * The page reads in the order the family does, which that list cannot give:
 * the Start/Top of Line couple first with their children — each the head of
 * a line — then line by line, oldest head to youngest, every descendant in
 * that line generation by generation with their own children under them,
 * "No children" included. That
 * shape is the family tree's own, so it comes off the tree the Family Tree
 * page draws — GET /family-tree-hierarchy-v2/{id}, asked for oldest-first
 * within each generation — where every node carries its name parts, dates,
 * status, line colour, the spouse it is drawn beside and its children. The
 * report's ages are laid over the tree's children by member id; a child the
 * report does not name gets an age counted from the tree's date of birth.
 *
 * The number the dashboard card and the page heading print is neither of
 * those: it is the dashboard's own statistic, "parents/everyone" —
 * `descendant_parents_and_children` over
 * `total_descendant_parents_and_children` from GET /dashboard — see
 * formatDescParentChildrenCount.
 */

export const EMPTY_REPORT = {
  title: "",
  totalParents: 0,
  totalChildren: 0,
  ageById: new Map(),
};

/** A descendant with no children of their own is still a parent-to-be from this age. */
export const DESC_PARENT_ADULT_AGE = 18;

/** Whole number or null — an age the payload does not name sorts last. */
const toNumberOrNull = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : null;
};

const trimPart = (value) => (value == null ? "" : String(value).trim());

/**
 * The first name as the report prints it. The tree can carry the whole given
 * name in the field — "Joseph Alexander", "Frances Virginia" — and the report
 * prints first names only, so only the leading word is kept; but where that
 * word is followed by a bare initial the initial stays, because it is not a
 * middle name but what tells two descendants of the same name apart —
 * "Mary M" heads one line, "Mary F" another — and every other list on the
 * dashboard, the family lines key included, spells them that way.
 */
const shortFirstName = (value) => {
  const [first, next] = trimPart(value).split(/\s+/);
  if (!first) return "";
  return /^[A-Za-z]\.?$/.test(next || "") ? `${first} ${next}` : first;
};

const recordId = (record) => trimPart(record?.id ?? record?._id ?? record?.member_id);

const isDeceasedRecord = (record) => Number(record?.present_or_deceased) === 2;

/** The rows print descendants the way every dashboard list does. */
const nameOptions = { unnamedLabel: "", appendDescendantSpouse: false };

/**
 * A descendant on their own, spelled the way one is on every dashboard row:
 * first name, then the suffix after a comma or the special initial after a
 * space — "Frank, Sr.", "Mary M", "Calvin". No surname.
 */
function formatShortName(member) {
  const first =
    shortFirstName(member?.first_name) || shortFirstName(member?.name ?? member?.full_name);
  if (!first) return "";
  const fullFirst = trimPart(member?.first_name);
  if (isUnnamedFirstName(fullFirst)) {
    return formatDashboardMemberDisplayName(member, nameOptions);
  }
  return getShortDisplayName(first, trimPart(member?.suffix), trimPart(member?.special_name));
}

/**
 * The spouse a tree node is drawn beside, as name parts — or null where the
 * node names none.
 */
function readNodeSpouse(node) {
  const first = shortFirstName(node?.spouse_first_name);
  if (!first) return null;
  return {
    first_name: first,
    last_name: trimPart(node?.spouse_last_name),
    suffix: trimPart(node?.spouse_suffix),
    special_name: trimPart(node?.spouse_special_name),
    present_or_deceased: node?.spouse_present_or_deceased,
  };
}

/**
 * A heading naming a couple, in the pieces the page prints it from: the
 * member's short name — first name with their own suffix or special initial,
 * "Paul, Sr.", "Mary M" — then the spouse's first and last name with the
 * spouse's own suffix: "Paul, Sr. & Linda Hawkins". Each suffix stays on the
 * person it belongs to, and the pieces come apart so the page can put each
 * person's markers — the angel, the twin tag — right after their own name.
 * Without a spouse it is the member alone, all in `primaryName`.
 *
 * `familySurname` prints the member's own surname after the spouse instead
 * of the spouse's: the Start/Top of Line couple carry the family's name —
 * "Joseph, Sr. & Frances Witherspoon" — where the spouse's record may hold
 * a maiden-and-married pairing like "Witherspoon-Marrs". `surname: false`
 * leaves the surname off altogether — "Joseph, Sr. & Frances" — so the
 * Start/Top couple fits on one line at the line headings' size.
 */
export function getDescParentHeadingParts(
  member,
  spouse,
  { familySurname = false, surname = true } = {}
) {
  const spouseFirst = shortFirstName(spouse?.first_name);
  const memberFirst = shortFirstName(member?.first_name);
  const primaryName = formatShortName(member);
  if (!spouseFirst || !memberFirst) {
    return { primaryName, spouseName: "" };
  }

  // A spouse recorded without a surname shares the member's, so the couple
  // still reads "John & Mary Doe" rather than "John & Mary".
  const spouseLast = !surname
    ? ""
    : familySurname
    ? trimPart(member?.last_name) || trimPart(spouse?.last_name)
    : trimPart(spouse?.last_name) || trimPart(member?.last_name);

  return {
    primaryName,
    spouseName: getShortDisplayName(
      [spouseFirst, spouseLast].filter(Boolean).join(" "),
      trimPart(spouse?.suffix),
      trimPart(spouse?.special_name)
    ),
  };
}

/** The heading as one string — what the search box reads. */
export function formatDescParentHeadingName(member, spouse, options) {
  const { primaryName, spouseName } = getDescParentHeadingParts(member, spouse, options);
  return spouseName ? `${primaryName} & ${spouseName}` : primaryName;
}

/**
 * The two figures the dashboard card and the page heading print, off the
 * dashboard statistics block: the family units — descendant parents — and
 * the individuals, descendant parents and children together.
 */
export function getDescParentChildrenCounts(statistics) {
  return {
    familyUnits: toNumberOrNull(statistics?.descendant_parents_and_children) ?? 0,
    individuals: toNumberOrNull(statistics?.total_descendant_parents_and_children) ?? 0,
  };
}

/**
 * The count as the page heading prints it, e.g. "72/184" — the same "a/b"
 * shape as the Males/Females card. The dashboard card names each figure.
 */
export function formatDescParentChildrenCount(statistics) {
  const { familyUnits, individuals } = getDescParentChildrenCounts(statistics);
  return `${familyUnits}/${individuals}`;
}

/**
 * The dashboard statistics block for one family tree — what the detail page
 * reads its heading count from, so it prints the number the card did. Null
 * when it cannot be read.
 */
export async function fetchDashboardStatistics(familyTreeId) {
  if (!familyTreeId) return null;

  const response = await getApi(
    `/dashboard?family_tree_id=${encodeURIComponent(familyTreeId)}`
  );
  if (response?.statusCode !== 200 || !response?.data) return null;

  return response.data.statistics ?? null;
}

/**
 * The report for one family tree: its counts, and every child's age by
 * member id. `totalParents` is the API's own count of parents.
 */
export async function fetchDescParentChildren(familyTreeId) {
  if (!familyTreeId) return EMPTY_REPORT;

  const response = await getApi(
    `/report-management/descendant-parents-and-children?family_tree_id=${encodeURIComponent(
      familyTreeId
    )}`
  );

  if (response?.statusCode !== 200 || !response?.data) return EMPTY_REPORT;

  const parents = Array.isArray(response.data.parents) ? response.data.parents : [];
  const ageById = new Map();
  let childCount = 0;
  parents.forEach((group) => {
    (Array.isArray(group?.children) ? group.children : []).forEach((child) => {
      childCount += 1;
      const id = recordId(child);
      const age = toNumberOrNull(child?.age);
      if (id && age !== null) ageById.set(id, age);
    });
  });

  return {
    title: response.data.title || "",
    totalParents: toNumberOrNull(response.data.total_parents) ?? parents.length,
    totalChildren: toNumberOrNull(response.data.total_children) ?? childCount,
    ageById,
  };
}

/**
 * The family tree's root nodes — the Start/Top of Line couple(s) — with every
 * generation nested under them in `children`, siblings oldest first. An empty
 * list when the tree cannot be read.
 */
export async function fetchDescParentFamilyTree(familyTreeId) {
  if (!familyTreeId) return [];

  const response = await getApi(
    `/family-tree-hierarchy-v2/${encodeURIComponent(familyTreeId)}`,
    { params: { values: "old" } }
  );
  if (response?.statusCode !== 200 || !response?.data) return [];

  if (Array.isArray(response.data.tree)) return response.data.tree;
  return Array.isArray(response.data) ? response.data : [];
}

/** Full years from the date of birth to today, alive or not. Null without a DOB. */
function readAgeToday(node) {
  const dobMs = getMemberDobTimestamp(node);
  if (dobMs === -1) return null;
  return toNumberOrNull(calculateAge(new Date(dobMs).toISOString().slice(0, 10)));
}

/** The age printed beside a child: the report's, else counted off the tree. */
function readAge(node, ageById) {
  const fromReport = ageById.get(recordId(node));
  if (fromReport !== undefined) return fromReport;

  if (isDeceasedRecord(node)) return getMemberAgeAtDeath(node);

  return readAgeToday(node);
}

/**
 * The age as the row prints it: the full years, or for a child under a year
 * old the months — "5 mos." — or days — "12 days" — counted off the date of
 * birth (to the date of death for one who died, unless `today` asks for the
 * age they would be now). Null when unknown.
 */
function readAgeLabel(node, age, { today = false } = {}) {
  if (age === null) return null;
  return getMemberAgeLabel(node, age, { toToday: today });
}

const nodeChildren = (node) => (Array.isArray(node?.children) ? node.children : []);

/**
 * One tree node → the child row printed under its parent. Under the
 * Start/Top of Line couple (`ageToday`) the age is the one the child would
 * be now, counted from their date of birth whether they are living or not,
 * so the heads of line read in one measure; everywhere else one who died
 * carries their age at death.
 */
function toChildRow(node, ageById, { ageToday = false } = {}) {
  const { baseName, descendantSpouseName } = getDashboardMemberNameParts(node, nameOptions);
  const printedName = baseName || formatShortName(node);
  const age = ageToday ? readAgeToday(node) ?? readAge(node, ageById) : readAge(node, ageById);

  return {
    id: recordId(node) || printedName,
    member: node,
    baseName: printedName,
    descendantSpouseName,
    sortName: `${printedName} ${descendantSpouseName}`.trim(),
    age,
    ageLabel: readAgeLabel(node, age, { today: ageToday }),
    isDeceased: isDeceasedRecord(node),
    colorName: trimPart(node?.color_name),
    hexCode: trimPart(node?.hex_code) || "#000000",
  };
}

/** One tree node → the group headed by that member, their children as rows. */
function toParentGroup(node, ageById) {
  const spouse = readNodeSpouse(node);

  return {
    id: recordId(node) || formatShortName(node),
    member: node,
    spouse,
    headingParts: getDescParentHeadingParts(node, spouse),
    headingName: formatDescParentHeadingName(node, spouse),
    sortName: formatShortName(node),
    isDeceased: isDeceasedRecord(node),
    spouseDeceased: isDeceasedRecord(spouse),
    childCount: nodeChildren(node).length,
    familyCount: nodeChildren(node).length + 1 + (spouse ? 1 : 0),
    colorName: trimPart(node?.color_name),
    hexCode: trimPart(node?.hex_code) || "#000000",
    rows: nodeChildren(node).map((child) => toChildRow(child, ageById)),
  };
}

/**
 * Everyone in a member's branch, as the family tree counts a line: each
 * descendant, and the spouse named beside them — in-laws included.
 */
function countBranchMembers(node) {
  return nodeChildren(node).reduce(
    (sum, child) => sum + countBranchMembers(child),
    1 + (readNodeSpouse(node) ? 1 : 0)
  );
}

/**
 * Whether a descendant stands as a parent in their line: anyone with
 * children, and any adult without — "No children" under their name — so the
 * line reads every parent and parent-to-be. A minor with no children appears
 * only as a child.
 */
function standsAsParent(row) {
  if (nodeChildren(row.member).length > 0) return true;
  return row.age !== null && row.age >= DESC_PARENT_ADULT_AGE;
}

/**
 * The page, built off the tree with the report's ages laid over it:
 *
 *   topOfLine — one entry per root: the Start/Top of Line couple as a
 *     heading in the root's own line colour, the parents their record names
 *     over it, and their children under it, each named with the line they
 *     head and aged as they would be today, living or not.
 *   lines — one per root child, in the tree's order (oldest head first):
 *     the head's short name and line, the line's total members, then the groups of that line read
 *     generation by generation — the head with their children, then each of
 *     those children with theirs, then the grandchildren, and so on down —
 *     siblings in birth order and cousins in their parents' order.
 */
export function buildDescParentChildrenPage(roots, ageById = new Map()) {
  const topOfLine = [];
  const lines = [];

  (Array.isArray(roots) ? roots : []).forEach((root) => {
    const spouse = readNodeSpouse(root);

    topOfLine.push({
      id: recordId(root) || formatShortName(root),
      member: root,
      // Printed without the surname; the search still reads it.
      headingParts: getDescParentHeadingParts(root, spouse, { surname: false }),
      headingName: formatDescParentHeadingName(root, spouse, { familySurname: true }),
      // The couple's own parents — the generation above the tree — as the
      // record names them in "Bio Descendant Parent(s)". The record holds
      // that field for the member alone; the spouse's parents have no
      // field yet, so only one side of the couple can name theirs.
      parentsName: trimPart(root?.bio_descendant_parent),
      isDeceased: isDeceasedRecord(root),
      spouseDeceased: isDeceasedRecord(spouse),
      childCount: nodeChildren(root).length,
      familyCount: nodeChildren(root).length + 1 + (spouse ? 1 : 0),
      colorName: trimPart(root?.color_name),
      hexCode: trimPart(root?.hex_code) || "#000000",
      rows: nodeChildren(root).map((child) => toChildRow(child, ageById, { ageToday: true })),
    });

    nodeChildren(root).forEach((head) => {
      const groups = [];
      const queue = [head];
      while (queue.length > 0) {
        const group = toParentGroup(queue.shift(), ageById);
        groups.push(group);
        group.rows.filter(standsAsParent).forEach((row) => queue.push(row.member));
      }

      lines.push({
        id: recordId(head) || formatShortName(head),
        member: head,
        headName: formatShortName(head),
        colorName: trimPart(head?.color_name),
        hexCode: trimPart(head?.hex_code) || "#000000",
        // Everyone in the line, as the family tree counts it: every
        // descendant and every spouse named beside one, in-laws included.
        memberCount: countBranchMembers(head),
        groups,
      });
    });
  });

  return { topOfLine, lines };
}

/**
 * The page as a flat run of blocks, in reading order: the Start/Top of Line
 * section as one block, then each line's heading and each of its groups as
 * blocks of their own. A block is the smallest thing a column split keeps
 * together — a parent is never parted from their children.
 */
export function toDescParentChildrenBlocks(page) {
  const blocks = [];

  page.topOfLine.forEach((section) => {
    blocks.push({ kind: "top", key: `top-${section.id}`, section });
  });

  page.lines.forEach((line) => {
    blocks.push({ kind: "line", key: `line-${line.id}`, line });
    line.groups.forEach((group) => {
      blocks.push({ kind: "group", key: `group-${line.id}-${group.id}`, line, group });
    });
  });

  return blocks;
}

/**
 * The blocks cut into two columns at `splitAt` — the first `splitAt` blocks,
 * then the rest — the way the page reads them: the cut never leaves a
 * line's heading as the last thing in the first column, and where it falls
 * inside a line the second column opens with that line's heading again,
 * marked `continued`, so the groups there still read under their line. A
 * cut at or past either end comes back as one column, the whole page.
 */
export function splitDescParentChildrenBlocks(blocks, splitAt) {
  let cut = splitAt;
  if (blocks[cut - 1]?.kind === "line") cut -= 1;
  if (cut <= 0 || cut >= blocks.length) return [blocks];

  const first = blocks.slice(0, cut);
  const second = blocks.slice(cut);
  if (second[0].kind === "group") {
    const { line } = second[0];
    second.unshift({
      kind: "line",
      key: `line-${line.id}-continued`,
      line,
      continued: true,
    });
  }

  return [first, second];
}
